import { useRef, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  type SearcherDocumentSearchFilter,
  type SearcherDocumentSearchSort,
  type FunnelStatus,
} from 'src/api/generated';
import { useFunnelConversationDocuments } from '../api/funnels/funnel-conversations';
import { useInfiniteScroll } from 'src/modules/shared/hooks/scroll/movement';
import { SearcherDocumentSearchFilterOperator } from 'src/api/generated';
import { type StatusContainerDragData } from '../../types/pipeline/dnd.types';
import { createPipelineContainerId } from '../../utils/pipeline/dnd.utils';

interface UseStatusColumnParams {
  status: FunnelStatus;
  selectedFunnelId?: number;
  searchTerm?: string;
  filters?: SearcherDocumentSearchFilter[];
  sorts?: SearcherDocumentSearchSort[];
  enabled?: boolean;
}

interface UseStatusColumnReturn {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  setRefs: (node: HTMLDivElement | null) => void;
  conversations: ReturnType<
    typeof useFunnelConversationDocuments
  >['conversations'];
  loading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  totalCount?: number;
}

interface StatusColumnSearchParams {
  status: FunnelStatus;
  selectedFunnelId?: number;
  searchTerm?: string;
  filters?: SearcherDocumentSearchFilter[];
  sorts?: SearcherDocumentSearchSort[];
}

function buildStatusColumnFilters({
  status,
  selectedFunnelId,
  filters = [],
}: Pick<
  StatusColumnSearchParams,
  'status' | 'selectedFunnelId' | 'filters'
>): SearcherDocumentSearchFilter[] {
  return [
    {
      field: 'funnelId',
      operator: SearcherDocumentSearchFilterOperator.EQ,
      value: selectedFunnelId?.toString(),
    },
    {
      field: 'funnelStatusId',
      operator: SearcherDocumentSearchFilterOperator.EQ,
      value: status.id.toString(),
    },
    ...filters,
  ];
}

function useStatusColumnDocumentSearch({
  status,
  selectedFunnelId,
  searchTerm,
  filters = [],
  sorts = [],
  enabled = true,
  size = 20,
}: StatusColumnSearchParams & {
  enabled?: boolean;
  size?: number;
}) {
  const statusColumnFilters = buildStatusColumnFilters({
    status,
    selectedFunnelId,
    filters,
  });

  return useFunnelConversationDocuments(
    {
      search: searchTerm,
      filters: statusColumnFilters,
      ...(sorts.length > 0 && { sort: sorts }),
      size,
      includeTotalCount: true,
    },
    enabled && !!selectedFunnelId
  );
}

export const useStatusColumnConversations = ({
  status,
  selectedFunnelId,
  searchTerm,
  filters = [],
  sorts = [],
  enabled = true,
}: UseStatusColumnParams): UseStatusColumnReturn => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerId = createPipelineContainerId(status.id);

  const { setNodeRef } = useDroppable({
    id: containerId,
    disabled: !enabled,
    data: {
      type: 'container',
      data: {
        status,
        statusId: status.id,
      },
      containerId,
    } satisfies StatusContainerDragData,
  });

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      containerRef.current = node;
    },
    [setNodeRef]
  );

  const {
    conversations,
    loading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    totalCount,
  } = useStatusColumnDocumentSearch({
    status,
    selectedFunnelId,
    searchTerm,
    filters,
    sorts,
    enabled,
    size: 20,
  });

  useInfiniteScroll({
    containerRef,
    isFetching: isFetchingNextPage,
    hasMore: hasNextPage,
    fetchMore: () => fetchNextPage(),
    direction: 'down',
    threshold: 0.3,
  });

  return {
    containerRef,
    setRefs,
    conversations,
    loading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    totalCount,
  };
};

export function useStatusColumnConversationCount({
  status,
  selectedFunnelId,
  searchTerm,
  filters = [],
  sorts = [],
  enabled = true,
}: UseStatusColumnParams): { count?: number; loading: boolean } {
  const { totalCount, loading } = useStatusColumnDocumentSearch({
    status,
    selectedFunnelId,
    searchTerm,
    filters,
    sorts,
    enabled,
    size: 1,
  });

  return {
    count: totalCount,
    loading,
  };
}
